import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useUser } from '@clerk/clerk-react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCalendarAlt, faUserFriends, faFlag } from '@fortawesome/free-solid-svg-icons';
import EventSkeletonLoader from './EventSkeletonLoader'; 
import LevelAndUserButton from './LevelAndUserButton';

function CurrentEvents() {
  const [events, setEvents] = useState([]); 
  const [view, setView] = useState('upcoming');
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  const { user } = useUser();

  useEffect(() => { 
      window.scrollTo(0, 0);
    }, []);

  // Set initial view based on location.state
  useEffect(() => {
    if (sessionStorage.getItem('fromLeaderboard') === '1') {
      setView('completed');
      sessionStorage.removeItem('fromLeaderboard');
    }
  }, []);

  useEffect(() => {
    setLoading(true);
    const endpoint =
      view === 'upcoming'
        ? '/server/crazy-cup-server/getUserUpcomingEvents.php'
        : '/server/crazy-cup-server/getUserCompletedEvents.php';

    // Fetch events based on the selected view
    const fetchData = async () => {
      const startTime = Date.now();
      try {
        const response = await fetch(endpoint, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ clerkUserId: user.id }),
        });
        const data = await response.json();
        if (data.success) {
          setEvents(data.data);
        } else {
          console.error('Error fetching events:', data.message);
        }
      } catch (error) {
        console.error('Error fetching events:', error);
      } finally {
        const elapsedTime = Date.now() - startTime; 
        const remainingTime = Math.max(200 - elapsedTime, 0);
        setTimeout(() => {
          setLoading(false); 
        }, remainingTime);
      }
    };

    fetchData();
  }, [view, user.id]);

  return (
    <div id="current-events-page" className="page">
      <h1 className='page-title'>EVENTS</h1>
      <div className="toggle-buttons">
        <button
          className={view === 'upcoming' ? 'active' : ''}
          onClick={() => setView('upcoming')}
        >
          Upcoming Events
        </button>
        <button
          className={view === 'completed' ? 'active' : ''}
          onClick={() => setView('completed')}
        >
          Completed Events
        </button>
      </div>
      {loading ? (
        // Use the skeleton loader while loading
        <EventSkeletonLoader />
      ) : events.length === 0 ? (
        <p>{view === 'upcoming' ? 'There are no upcoming events.' : 'There are no completed events.'}</p>
      ) : (
        <ul>
          {events.map((event) => {
            const formattedDate = new Date(event.date).toLocaleDateString('en-GB', {
              weekday: 'long',
              day: 'numeric',
              month: 'long',
            });
            const daySuffix = (day) => {
              if (day > 3 && day < 21) return 'th';
              switch (day % 10) {
                case 1: return 'st';
                case 2: return 'nd';
                case 3: return 'rd';
                default: return 'th';
              }
            };
            const day = new Date(event.date).getDate();
            const formattedDateWithSuffix = formattedDate.replace(day, `${day}${daySuffix(day)}`);

            return (
              <div
                className="event-box"
                key={event.id}
                onClick={() => {
                  if (view === 'completed') {
                    sessionStorage.setItem('fromLeaderboard', '1');
                    navigate(`/event/${event.id}/leaderboard`);
                  } else {
                    navigate(`/event/${event.id}`);
                  }
                }}
              >
                <div className="event-header">
                  <strong>{event.cup_name.toUpperCase()}</strong>
                </div>
                <div className="event-body">
                  <p><FontAwesomeIcon icon={faCalendarAlt} className="fa-icon" /> {formattedDateWithSuffix}</p>
                  <p><FontAwesomeIcon icon={faUserFriends} className="fa-icon" /> Players: {JSON.parse(event.players).length}</p>
                  <p><FontAwesomeIcon icon={faFlag} className="fa-icon" /> Par: {event.total_par}</p>
                </div>
              </div>
            );
          })}
        </ul>
      )}
      <button
        id="back-to-main"
        className="back-to-main"
        onClick={() => navigate('/')}
      >
        Back
      </button>
    </div>
  );
}

export default CurrentEvents;